import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { db, auth, functions } from '../firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs, query, where, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { formatPrice } from '../utils/priceFormatter';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createUserWithEmailAndPassword } from 'firebase/auth';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  color: #555;
  cursor: pointer;
  margin-bottom: 2rem;
  font-size: 1rem;
  
  &:hover {
    color: #FF1493;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
`;

const ArtworkImage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto;
  object-fit: contain;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ArtistLink = styled(Link)`
  font-size: 1.5rem;
  color: #000;
  margin-bottom: 0.5rem;
  text-decoration: none;
  
  &:hover {
    color: #FF1493;
  }
`;

const ArtworkTitle = styled.h1`
  font-size: 1.75rem;
  margin: 0 0 1rem 0;
  font-weight: normal;
`;

const InfoText = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  line-height: 1.4;
`;

const Description = styled.p`
  margin: 1rem 0;
  line-height: 1.6;
  max-width: 600px;
  font-style: italic;
`;

const PriceText = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0;
`;

const SoldBadge = styled.span`
  display: inline-block;
  background-color: #000;
  color: #fff;
  padding: 0.25rem 0.5rem;
  margin-right: 0.5rem;
`;

const Divider = styled.hr`
  margin: 1.5rem 0;
  border: none;
  border-top: 1px solid #eee;
`;

const Section = styled.div`
  margin: 1.5rem 0;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Button = styled.button`
  background-color: #FF1493;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #ff0080;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const EditButton = styled(Button)`
  background-color: #FF1493;
  margin-bottom: 1rem;
`;

const DeleteButton = styled(Button)`
  background-color: white;
  color: black;
  border: 1px solid black;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const ForSaleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
`;

const ForSaleText = styled.span`
  font-size: 1.2rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 26px;
      width: 26px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: #FF1493;
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  color: red;
  margin: 0.5rem 0;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  
  &.total {
    font-weight: bold;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #eee;
  }
`;

const PaymentSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: #f9f9f9;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const CardElementContainer = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  
  /* Fix for Stripe Elements ARIA warning */
  & .StripeElement {
    &[aria-hidden="true"] {
      display: none;
    }
  }
`;

const PaymentForm = ({ onSubmit, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    onSubmit(stripe, elements);
  };

  return (
    <form onSubmit={handleSubmit}>
      <CardElementContainer>
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }} />
      </CardElementContainer>
      <Button type="submit" disabled={!stripe || disabled}>
        {disabled ? 'Processing...' : 'Pay Now'}
      </Button>
    </form>
  );
};

const ExhibitionsSection = styled.div`
  margin: 1.5rem 0;
`;

const ExhibitionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ExhibitionBanner = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; // 16:9 aspect ratio
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
  background-color: #f5f5f5;

  &:hover {
    transform: scale(1.02);
  }
`;

const ExhibitionBannerImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
`;

const ArtworkDetailsScreenContent = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [isForSale, setIsForSale] = useState(false);
  
  // Payment state
  const [email, setEmail] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
  });
  const [isCalculatingTax, setIsCalculatingTax] = useState(false);
  const [priceInfo, setPriceInfo] = useState({
    basePrice: 0,
    taxAmount: 0,
    totalAmount: 0
  });
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [user, setUser] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [exhibitions, setExhibitions] = useState([]);

  const isCurrentUserArtistAndOwner = artwork?.artist?.id === auth.currentUser?.uid && 
                                    artwork?.owner?.id === auth.currentUser?.uid;

  const handleEditListing = () => {
    if (!artwork) return;

    // Extract category IDs and names
    const categoryIds = artwork.categories.map(cat => cat.id);
    const categoryNames = artwork.categories.map(cat => cat.name || cat.id);

    const serializableArtworkData = {
      id: artwork.id,
      title: artwork.title || '',
      createdYear: artwork.createdYear || '',
      categoryIds,
      categoryNames,
      isPublicArt: artwork.isPublicArt || false,
      isForSale: artwork.isForSale || false,
      price: artwork.price || 0,
      materials: artwork.materials || '',
      size: artwork.size || '',
      description: artwork.description || '',
      allowBuyerPickUp: artwork.allowBuyerPickUp || false,
      zipCode: artwork.zipCode || '',
      image: artwork.imageOriginal || '',
      status: artwork.status || 'draft',
    };

    navigate('/upload', {
      state: {
        editMode: true,
        artworkData: serializableArtworkData,
        existingArtworkId: artwork.id,
        originalImageUrl: artwork.imageOriginal,
        currentUserId: artwork.owner?.id,
        artistName: artwork.artistData?.displayName,
        artistBirthYear: artwork.artistData?.birthYear,
      },
    });
  };

  const handleDeleteListing = async () => {
    if (window.confirm("Are you sure you want to delete this listing? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, 'artworks', artwork.id));
        alert("Listing has been deleted.");
        navigate('/');
      } catch (error) {
        console.error("Error deleting listing:", error);
        alert("Failed to delete listing. Please try again.");
      }
    }
  };

  const handleToggleForSale = async (event) => {
    const newValue = event.target.checked;
    setIsForSale(newValue);
    try {
      await updateDoc(doc(db, 'artworks', artwork.id), { isForSale: newValue });
      setArtwork(prev => ({ ...prev, isForSale: newValue }));
    } catch (error) {
      console.error("Error updating isForSale status:", error);
      alert("Failed to update listing status. Please try again.");
      setIsForSale(!newValue); // Revert the switch if update fails
    }
  };

  useEffect(() => {
    // Get logged in user if available
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        setEmail(currentUser.email);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchArtworkDetails();
  }, [artworkId]);

  const fetchExhibitions = async (exhibitionRefs) => {
    if (!exhibitionRefs || exhibitionRefs.length === 0) return;
    
    try {
      const exhibitionPromises = exhibitionRefs.map(async (ref) => {
        const exhibitionDoc = await getDoc(ref);
        if (exhibitionDoc.exists()) {
          return {
            id: exhibitionDoc.id,
            ...exhibitionDoc.data()
          };
        }
        return null;
      });

      const exhibitionResults = await Promise.all(exhibitionPromises);
      const validExhibitions = exhibitionResults.filter(exhibition => exhibition !== null);
      setExhibitions(validExhibitions);
    } catch (error) {
      console.error("Error fetching exhibitions:", error);
    }
  };

  const fetchArtworkDetails = async () => {
    if (!artworkId) return;

    try {
      setLoading(true);
      const artworkRef = doc(db, 'artworks', artworkId);
      const artworkDoc = await getDoc(artworkRef);

      if (!artworkDoc.exists()) {
        setError('Artwork not found');
        return;
      }

      const artworkData = { id: artworkDoc.id, ...artworkDoc.data() };
      setArtwork(artworkData);

      // Fetch artist data
      if (artworkData.artist) {
        const artistDoc = await getDoc(artworkData.artist);
        if (artistDoc.exists()) {
          setArtist({ id: artistDoc.id, ...artistDoc.data() });
        }
      }

      // Fetch exhibitions if they exist
      if (artworkData.exhibitions && artworkData.exhibitions.length > 0) {
        await fetchExhibitions(artworkData.exhibitions);
      }

      // Handle categories
      if (artworkData.categories) {
        artworkData.categories = artworkData.categories.filter(cat => cat && cat.id);
      } else {
        artworkData.categories = [];
      }

      setIsForSale(artworkData.isForSale);
      
      if (artworkData.price) {
        setPriceInfo({
          basePrice: artworkData.price,
          taxAmount: 0,
          totalAmount: artworkData.price
        });
      }
    } catch (error) {
      console.error("Error fetching artwork details:", error);
      setError('Failed to fetch artwork details');
    } finally {
      setLoading(false);
    }
  };

  const validateShippingAddress = () => {
    const { line1, city, state, postal_code } = shippingAddress;
    return (
      line1.trim() !== '' &&
      city.trim() !== '' &&
      state.trim() !== '' &&
      postal_code.length === 5 &&
      email.includes('@')
    );
  };

  const handleAddressChange = (field, value) => {
    const newAddress = {
      ...shippingAddress,
      [field]: value
    };
    
    setShippingAddress(newAddress);

    // Check if all address fields are filled after this change
    if (newAddress.line1 && 
        newAddress.city && 
        newAddress.state && 
        newAddress.postal_code && 
        newAddress.postal_code.length === 5 &&
        email && 
        email.includes('@')) {
      // Calculate tax when address is complete
      calculateTax();
    }
  };

  const calculateTax = async () => {
    try {
      setIsCalculatingTax(true);
      setPurchaseError('');
      
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntentV2');
      const result = await createPaymentIntent({
        amount: artwork.price,
        artworkId: artwork.id,
        artistStripeConnectAccountId: artwork.artistData?.stripeConnectAccountId,
        purchaserId: user?.uid,
        purchaserName: user?.displayName,
        purchaserStripeId: user?.stripeId,
        purchaserEmail: !user ? email : undefined,
        customerAddress: shippingAddress,
      });

      if (!result.data) {
        throw new Error('Invalid response from server');
      }

      setPriceInfo({
        basePrice: result.data.preTaxAmount ? result.data.preTaxAmount / 100 : artwork.price,
        taxAmount: result.data.taxAmount ? result.data.taxAmount / 100 : 0,
        totalAmount: result.data.totalAmount ? result.data.totalAmount / 100 : artwork.price
      });
    } catch (error) {
      console.error('Error calculating tax:', error);
      setPurchaseError('Failed to calculate tax. Please check your shipping address and try again.');
    } finally {
      setIsCalculatingTax(false);
    }
  };

  const handleBuyNow = () => {
    setShowPaymentSection(true);
  };

  const formatArtworkSize = (size) => {
    return size || 'Size not specified';
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate back to previous page
  };

  const handlePayment = async (stripe, elements) => {
    if (!validateShippingAddress()) {
      setPurchaseError('Please fill in all shipping address fields');
      return;
    }

    if (!user && !email) {
      setPurchaseError('Please enter your email address to continue with the purchase');
      return;
    }

    try {
      setProcessingPayment(true);
      setPurchaseError('');
      
      let userId = user?.uid;
      let purchaserName = user?.displayName;
      let purchaserStripeId = user?.stripeId;
      let additionalMessage = '';

      // If user is not logged in, handle anonymous purchase
      if (!user) {
        try {
          // Check if user exists with this email
          const userQuery = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
          
          if (userQuery.empty) {
            // Create new user with random password
            const userCredential = await createUserWithEmailAndPassword(auth, email, Math.random().toString(36).slice(-8));
            userId = userCredential.user.uid;
            purchaserName = email.split('@')[0]; // Use part of email as name
            
            // Create user document
            await setDoc(doc(db, 'users', userId), {
              email: email,
              displayName: purchaserName,
              createdAt: serverTimestamp(),
              lastModified: serverTimestamp(),
              bio: "Collector needs to set their bio.",
              shippingAddress: shippingAddress
            });
            
            additionalMessage = "An account was created for you. You can set your password from the Login screen using the 'Reset Password' button.";
          } else {
            // Use existing user
            const userDoc = userQuery.docs[0];
            userId = userDoc.id;
            purchaserName = userDoc.data().displayName;
            purchaserStripeId = userDoc.data().stripeId;
            additionalMessage = "An account was found with your email address. You can reset your password from the Login screen.";
          }
        } catch (error) {
          console.error('Error creating/finding user account:', error);
          setPurchaseError('Error creating user account: ' + error.message);
          setProcessingPayment(false);
          return;
        }
      }
      
      // Create payment intent
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntentV2');
      const result = await createPaymentIntent({
        amount: artwork.price,
        artworkId: artwork.id,
        artistId: artwork.artistData?.id,
        artistName: artwork.artistData?.displayName,
        purchaserId: userId,
        purchaserName: purchaserName,
        artistStripeConnectAccountId: artwork.artistData?.stripeConnectAccountId,
        purchaserStripeId: purchaserStripeId,
        purchaserEmail: !user ? email : undefined,
        customerAddress: shippingAddress,
      });

      if (!result.data || !result.data.clientSecret) {
        throw new Error('Invalid payment intent response');
      }

      const { clientSecret, paymentIntentId } = result.data;

      // Confirm the payment with Stripe
      const { error: stripeError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: purchaserName || email,
            email: email
          }
        }
      });

      if (stripeError) {
        throw stripeError;
      }

      // Complete the purchase
      const purchaseArtwork = httpsCallable(functions, 'purchaseArtworkV2');
      const purchaseResult = await purchaseArtwork({
        artworkId: artwork.id,
        paymentIntentId: paymentIntentId,
        userId: userId,
        customerAddress: shippingAddress,
      });

      // Save shipping address for future use
      try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          shippingAddress,
          lastModified: new Date()
        });
      } catch (error) {
        console.error('Error saving address:', error);
      }

      // Navigate to success page with additional message if needed
      navigate('/purchase-success', { 
        state: { additionalMessage } 
      });

    } catch (error) {
      console.error('Error processing payment:', error);
      setPurchaseError(error.message || 'Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return <LoadingIndicator>Loading artwork details...</LoadingIndicator>;
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <Button onClick={() => navigate('/')}>Return to Marketplace</Button>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={handleGoBack}>← Back</BackButton>

      <ContentGrid>
        <ImageContainer>
          <ArtworkImage 
            src={artwork.imageMedium || artwork.imageOriginal} 
            alt={artwork.title} 
          />
        </ImageContainer>

        <InfoContainer>
          <ArtistLink to={`/artist/${artwork.artist?.id}`}>
            {artwork.artistData?.displayName || 'Unknown Artist'}
          </ArtistLink>
          
          <ArtworkTitle>
            {artwork.title || 'Untitled'}, {artwork.createdYear || 'Year Unknown'}
          </ArtworkTitle>
          
          {exhibitions.length > 0 && (
            <ExhibitionsSection>
              <SectionTitle>Currently on Display</SectionTitle>
              <ExhibitionsGrid>
                {exhibitions.map((exhibition) => (
                  <ExhibitionBanner 
                    key={exhibition.id}
                    onClick={() => navigate(`/exhibition/${exhibition.id}`)}
                  >
                    {exhibition.banner && (
                      <ExhibitionBannerImage 
                        src={exhibition.banner} 
                        alt={exhibition.name}
                      />
                    )}
                  </ExhibitionBanner>
                ))}
              </ExhibitionsGrid>
            </ExhibitionsSection>
          )}
          
          <InfoText>{artwork.materials || 'Materials not specified'}</InfoText>
          <InfoText>{formatArtworkSize(artwork.size)}</InfoText>
          
          {!artwork.isForSale ? (
            <div>
              <SoldBadge>SOLD</SoldBadge>
              <PriceText>{formatPrice(artwork.price)}</PriceText>
            </div>
          ) : (
            <>
              <PriceText>{formatPrice(artwork.price)}</PriceText>
              {!isCurrentUserArtistAndOwner && (
                <Button onClick={handleBuyNow}>Buy Now</Button>
              )}
            </>
          )}

          {artwork.description && (
            <>
              <Divider />
              <Description>{artwork.description}</Description>
            </>
          )}

          {isCurrentUserArtistAndOwner && (
            <>
              <Divider />
              <EditButton onClick={handleEditListing}>
                Edit Listing
              </EditButton>
              <DeleteButton onClick={handleDeleteListing}>
                Delete Listing
              </DeleteButton>
              <ForSaleContainer>
                <ForSaleText>For Sale</ForSaleText>
                <ToggleSwitch>
                  <input
                    type="checkbox"
                    checked={isForSale}
                    onChange={handleToggleForSale}
                  />
                  <span />
                </ToggleSwitch>
              </ForSaleContainer>
            </>
          )}

          {showPaymentSection && (
            <PaymentSection>
              <SectionTitle>Shipping Address</SectionTitle>
              
              {purchaseError && <ErrorMessage>{purchaseError}</ErrorMessage>}
              
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!user}
              />
              
              <Input
                placeholder="Street Address"
                value={shippingAddress.line1}
                onChange={(e) => handleAddressChange('line1', e.target.value)}
              />
              
              <Input
                placeholder="City"
                value={shippingAddress.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
              />
              
              <Input
                placeholder="State"
                value={shippingAddress.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
              />
              
              <Input
                placeholder="ZIP Code"
                value={shippingAddress.postal_code}
                onChange={(e) => handleAddressChange('postal_code', e.target.value)}
              />
              
              <div style={{ marginTop: '1rem' }}>
                <PriceRow>
                  <span>Price:</span>
                  <span>{formatPrice(artwork.price)}</span>
                </PriceRow>
                {isCalculatingTax ? (
                  <LoadingIndicator>Calculating tax...</LoadingIndicator>
                ) : (
                  <>
                    <PriceRow>
                      <span>Tax:</span>
                      <span>{formatPrice(priceInfo.taxAmount)}</span>
                    </PriceRow>
                    <PriceRow className="total">
                      <span>Total:</span>
                      <span>{formatPrice(priceInfo.totalAmount)}</span>
                    </PriceRow>
                  </>
                )}
                
                <PaymentForm 
                  onSubmit={handlePayment}
                  disabled={processingPayment || isCalculatingTax || !priceInfo.totalAmount}
                />
              </div>
            </PaymentSection>
          )}
        </InfoContainer>
      </ContentGrid>

      {processingPayment && (
        <LoadingOverlay>
          <LoadingIndicator>Processing payment...</LoadingIndicator>
        </LoadingOverlay>
      )}
    </Container>
  );
};

const ArtworkDetailsScreen = () => {
  return (
    <Elements stripe={stripePromise}>
      <ArtworkDetailsScreenContent />
    </Elements>
  );
};

export default ArtworkDetailsScreen;